/**
 * VendorProjectDetail — /v4/vendor/projects/:id
 *
 * Sections:
 *   1. Header: project name, contractor, vendor status badge
 *   2. Project info card: address, status
 *   3. Documents: required slots (license/insurance/W9) + other uploads
 *   4. Upload modal
 *   5. Contact contractor (mailto link)
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Building2, MapPin, FileText, Upload,
  CheckCircle2, Clock, XCircle, Mail,
} from 'lucide-react'
import { fetchMyProjects, fetchMyDocuments, uploadVendorDocument, fetchVendorProjectDetail, contractorLogoUrl } from '../../api/vendor'
import type { VendorDocument } from '../../api/vendor'
import { vendorDocStatusDisplay } from './vendorDocStatus'
import { track } from '../../lib/track'
import DocUploadModal from '../../components/docs/DocUploadModal'

// ── Required doc types ────────────────────────────────────────────────────

const REQUIRED_DOC_TYPES = [
  { type: 'license',   label: 'License' },
  { type: 'insurance', label: 'Insurance Certificate' },
  { type: 'w9',        label: 'W-9 Form' },
]

// ── Status helpers ────────────────────────────────────────────────────────

function DocStatusBadge({ status, paidAt }: { status: string; paidAt?: string | null }) {
  const cfg = vendorDocStatusDisplay(status, paidAt)
  const Icon =
    paidAt || status === 'approved' ? CheckCircle2 :
    status === 'rejected'       ? XCircle      :
    Clock
  return (
    <span className={`chip ${cfg.chipClass}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
      <Icon size={11} />
      {cfg.label}
    </span>
  )
}

// ── Upload Modal ──────────────────────────────────────────────────────────

// ── Required doc slot ─────────────────────────────────────────────────────

function RequiredDocSlot({
  label, doc, onUpload,
}: {
  docType: string
  label:   string
  doc:     VendorDocument | undefined
  onUpload: () => void
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: '1px solid var(--line)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <FileText size={14} color="var(--ink4)" />
        <div>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{label}</div>
          {doc && (
            <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 2 }}>
              {doc.original_name} · <DocStatusBadge status={doc.status} paidAt={doc.paid_at} />
            </div>
          )}
        </div>
      </div>
      <button
        className={doc ? 'btn-ghost' : 'btn-primary'}
        style={{ fontSize: 12, padding: '5px 12px' }}
        onClick={onUpload}
      >
        {doc ? 'Replace' : 'Upload'}
      </button>
    </div>
  )
}

// ── Main screen ───────────────────────────────────────────────────────────

export default function VendorProjectDetail() {
  const { id }  = useParams<{ id: string }>()
  const projectId = Number(id)
  const navigate  = useNavigate()

  // Fire vendor_project_detail_viewed event on server
  useEffect(() => {
    fetchVendorProjectDetail(projectId).catch(() => {})
  }, [projectId])

  const [uploadModal, setUploadModal] = useState<{ open: boolean; defaultType: string }>({
    open: false, defaultType: 'other',
  })
  const qc = useQueryClient()

  const { data: projects, isLoading: projLoading } = useQuery({
    queryKey:  ['vendor-my-projects'],
    queryFn:   fetchMyProjects,
    staleTime: 30_000,
  })

  const { data: allDocs, isLoading: docsLoading } = useQuery({
    queryKey:  ['vendor-my-documents'],
    queryFn:   fetchMyDocuments,
    staleTime: 30_000,
  })

  const project = projects?.find(p => p.id === projectId)
  const projectDocs = (allDocs ?? []).filter(d => d.project_id === projectId)

  const getDocByType = (type: string) =>
    projectDocs
      .filter(d => d.doc_type === type)
      .sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime())[0]

  const invoiceDocs = projectDocs.filter(d => d.doc_type === 'invoice')
  const otherDocs = projectDocs.filter(d => !REQUIRED_DOC_TYPES.map(r => r.type).includes(d.doc_type) && d.doc_type !== 'invoice')

  if (projLoading) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div className="skel" style={{ width: 120, height: 14, marginBottom: 20 }} />
        <div className="skel" style={{ width: '60%', height: 22, marginBottom: 8 }} />
        <div className="skel" style={{ width: '40%', height: 14 }} />
      </div>
    )
  }

  if (!project) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <button className="btn-ghost" onClick={() => navigate('/vendor/dashboard')} style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
          <ArrowLeft size={14} /> Back
        </button>
        <p style={{ color: 'var(--red)', fontSize: 13 }}>Project not found or you don't have access.</p>
      </div>
    )
  }

  return (
    <>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Back nav */}
        <button
          className="btn-ghost"
          onClick={() => navigate('/vendor/dashboard')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 20, padding: '4px 0' }}
        >
          <ArrowLeft size={13} /> Back to projects
        </button>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
              {project.name}
            </h1>
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: project.vendor_status === 'active' ? 'var(--green)' : 'var(--amber)',
              border: `1px solid ${project.vendor_status === 'active' ? 'var(--green)' : 'var(--amber)'}`,
              borderRadius: 4,
              padding: '3px 10px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              {project.vendor_status === 'active' ? 'Active' : 'Invited'}
            </span>
          </div>
        </div>

        {/* Contractor brand card */}
        <div className="card" style={{ padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14, borderLeft: '3px solid var(--ink)' }}>
          {project.contractor_has_logo ? (
            <img
              src={contractorLogoUrl(project.id)}
              alt=""
              style={{ width: 46, height: 46, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--line)', background: '#fff', padding: 3, flexShrink: 0 }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <div style={{
              width: 46, height: 46, borderRadius: 8, background: 'var(--ink)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16, fontWeight: 700, flexShrink: 0,
            }}>
              {(project.contractor_company_name || project.contractor_name || '?').split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('')}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
              {project.contractor_company_name || project.contractor_name}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', fontSize: 12, color: 'var(--ink3)', marginTop: 3 }}>
              {project.contractor_company_name && project.contractor_name
                && project.contractor_company_name !== project.contractor_name && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Building2 size={12} /> {project.contractor_name}
                </span>
              )}
              {project.address && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <MapPin size={12} /> {project.address}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Invoices — the most common upload gets its own card */}
        <div className="card" style={{ padding: '20px', marginBottom: 24 }} data-testid="vendor-invoices-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <h2 style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Invoices
            </h2>
            <button
              className="btn b-dk"
              style={{ fontSize: 12, padding: '7px 16px', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
              onClick={() => setUploadModal({ open: true, defaultType: 'invoice' })}
              data-testid="vendor-upload-invoice"
            >
              <Upload size={12} /> Upload invoice
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--ink4)', margin: '0 0 12px' }}>
            Send an invoice to your contractor for this project
          </p>
          {invoiceDocs.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--ink5)', margin: 0 }}>No invoices submitted yet.</p>
          )}
          {invoiceDocs.map(doc => (
            <div key={doc.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 0', borderBottom: '1px solid var(--line)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FileText size={13} color="var(--ink4)" />
                <div>
                  <div style={{ fontSize: 13, color: 'var(--ink)' }}>{doc.original_name}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 1 }}>
                    <DocStatusBadge status={doc.status} paidAt={doc.paid_at} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Documents section */}
        <div className="card" style={{ padding: '20px', marginBottom: 24 }}>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Required Documents
          </h2>
          <p style={{ fontSize: 12, color: 'var(--ink4)', margin: '0 0 16px' }}>
            Upload these documents for your contractor
          </p>

          {REQUIRED_DOC_TYPES.map(({ type, label }) => (
            <RequiredDocSlot
              key={type}
              docType={type}
              label={label}
              doc={getDocByType(type)}
              onUpload={() => setUploadModal({ open: true, defaultType: type })}
            />
          ))}

          {/* Other documents */}
          {(otherDocs.length > 0 || true) && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h3 style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Other Documents
                </h3>
                <button
                  className="btn-ghost"
                  style={{ fontSize: 11, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => setUploadModal({ open: true, defaultType: 'other' })}
                >
                  <Upload size={11} /> Upload
                </button>
              </div>

              {docsLoading && <div className="skel" style={{ width: '100%', height: 40 }} />}

              {!docsLoading && otherDocs.length === 0 && (
                <p style={{ fontSize: 12, color: 'var(--ink5)', margin: 0 }}>
                  No other documents uploaded yet.
                </p>
              )}

              {otherDocs.map(doc => (
                <div key={doc.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--line)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileText size={13} color="var(--ink4)" />
                    <div>
                      <div style={{ fontSize: 13, color: 'var(--ink)' }}>{doc.original_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 1 }}>
                        {doc.doc_type} · <DocStatusBadge status={doc.status} paidAt={doc.paid_at} />
                        {doc.uploaded_by_role === 'contractor' && doc.filed_by_company
                          ? ` · Filed by ${doc.filed_by_company}`
                          : ''}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact contractor */}
        <div className="card" style={{ padding: '16px 20px' }}>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Contact Contractor
          </h2>
          <a
            href={`mailto:${project.contractor_email}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 13, color: 'var(--accent)', textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            <Mail size={13} />
            {project.contractor_email}
          </a>
        </div>
      </div>

      {/* Upload modal */}
      {uploadModal.open && (
        <DocUploadModal
          defaultType={uploadModal.defaultType}
          showInsuranceExpiry
          onClose={() => setUploadModal({ open: false, defaultType: 'other' })}
          onSuccess={() => {
            setUploadModal({ open: false, defaultType: 'other' })
            qc.invalidateQueries({ queryKey: ['vendor-my-documents'] })
          }}
          upload={async (file, docType, opts) => {
            await uploadVendorDocument(projectId, file, docType, opts)
            track('upload_received', { project_id: projectId })
          }}
        />
      )}
    </>
  )
}

